import Dexie, { type Table } from 'dexie'
import type { HouseholdEvent } from '@shared/events'

/** Las filas del servidor traen número de secuencia; las locales todavía no. */
export type StoredEvent = HouseholdEvent & { serverSeq?: number }

export interface OutboxEntry {
  /** Autoincremental: la cola se procesa en orden de entrada. */
  key?: number
  eventId: string
  /** El evento tal como estaba al encolarlo. */
  event: HouseholdEvent
  queuedAt: string
  attempts: number
  lastError?: string
}

export interface MetaEntry {
  key: string
  value: unknown
}

export interface HouseholdSnapshot {
  household: {
    id: string
    name: string
    timezoneMode: 'device' | 'fixed'
    timezone: string
  } | null
  members: Array<{ id: string; displayName: string; username: string }>
}

class RecetarioDatabase extends Dexie {
  events!: Table<StoredEvent, string>
  outbox!: Table<OutboxEntry, number>
  meta!: Table<MetaEntry, string>

  constructor(name = 'recetariodelucia') {
    super(name)
    this.version(1).stores({
      events: 'id, type, occurredAt, updatedAt, deletedAt',
      outbox: '++key, eventId, queuedAt',
      meta: 'key',
    })
  }
}

export const db = new RecetarioDatabase()

export async function readMeta<T>(key: string, fallback: T): Promise<T> {
  const row = await db.meta.get(key)
  return row ? (row.value as T) : fallback
}

export async function writeMeta(key: string, value: unknown): Promise<void> {
  await db.meta.put({ key, value })
}

/** Borra todo rastro local; se usa al salir, para que un móvil compartido no guarde nada. */
export async function clearLocalData(): Promise<void> {
  await db.transaction('rw', db.events, db.outbox, db.meta, async () => {
    await db.events.clear()
    await db.outbox.clear()
    await db.meta.clear()
  })
}

const OWNER_KEY = 'household'

/**
 * El espejo local es de un solo hogar. Si entra alguien de otro hogar en este
 * navegador sin que el anterior saliera (la sesión caducó, o se entró con
 * otra cuenta), se borra antes de sincronizar: si no, vería los datos del
 * otro hogar, su cola de salida acabaría escrita en el hogar nuevo y el
 * cursor se saltaría lo que no ha bajado nunca.
 *
 * Alguien del mismo hogar conserva el espejo: los datos son los mismos. Un
 * espejo sin dueño anotado es de antes de que hubiera más de un hogar, así
 * que era del único que existía: se le pone dueño sin borrar nada, para no
 * perder la cola de salida de un móvil que actualiza sin cobertura.
 */
export async function adoptHousehold(householdId: string): Promise<void> {
  const current = await readMeta<string | null>(OWNER_KEY, null)
  if (current === householdId) return
  if (current !== null) await clearLocalData()
  await writeMeta(OWNER_KEY, householdId)
}
